import React from 'react';

import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import {
  CircularProgress,
  InputAdornment,
  Stack,
  TextField,
  type TextFieldProps,
  Tooltip,
  Typography,
} from '@mui/material';

import { type PromoCodeAvailabilityCheck, usePromoCodeCheck } from '~/hooks/apis/promo/usePromoCodeCheck';

type Props = TextFieldProps & {
  validatePromoName?: boolean;
  multipleNames?: boolean;
  availability?: PromoCodeAvailabilityCheck;
};

export default function PromoCodeNameField({
  validatePromoName = true,
  multipleNames = false,
  availability,
  value,
  error,
  InputProps,
  ...props
}: Props) {
  const name = typeof value === 'string' ? value : '';
  const { validation, isCheckingAvailability } = usePromoCodeCheck({
    value: name,
    enabled: validatePromoName,
    multipleNames,
    availability,
  });
  const message = isCheckingAvailability ? 'Checking availability…' : validation?.message;

  return (
    <TextField
      {...props}
      value={value}
      error={error || validation?.isValid === false}
      InputProps={{
        ...InputProps,
        endAdornment: (
          <>
            {InputProps?.endAdornment}
            {(validation || isCheckingAvailability) && (
              <InputAdornment position="end" sx={{ maxWidth: '60%' }}>
                <Stack direction="row" spacing={0.5} alignItems="center" role="status" sx={{ minWidth: 0 }}>
                  {isCheckingAvailability ? (
                    <CircularProgress size={16} />
                  ) : validation?.isValid ? (
                    <CheckCircleOutlineIcon color="success" />
                  ) : (
                    <HighlightOffIcon color="error" />
                  )}
                  <Tooltip title={message}>
                    <Typography
                      variant="caption"
                      color={
                        isCheckingAvailability ? 'text.secondary' : validation?.isValid ? 'success.main' : 'error.main'
                      }
                      noWrap
                    >
                      {message}
                    </Typography>
                  </Tooltip>
                </Stack>
              </InputAdornment>
            )}
          </>
        ),
      }}
    />
  );
}
